import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, endpoints, storage, type User } from './api';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  register: (payload: { full_name: string; whatsapp_number: string; password: string; preferred_language: 'en' | 'ur' }) => Promise<{ whatsapp_number_masked: string }>;
  refresh: () => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(storage.user());
  const [loading, setLoading] = useState(Boolean(storage.token()));

  const refresh = async () => {
    if (!storage.token()) { setLoading(false); return; }
    try {
      const current = await api<User>(endpoints.me);
      localStorage.setItem('munshi_web_user', JSON.stringify(current));
      setUser(current);
    } catch { storage.clearAuth(); setUser(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { void refresh(); }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login: async (identifier, password) => {
      const result = await api<{ access_token: string; user: User }>(endpoints.login, {
        method: 'POST', auth: false, body: JSON.stringify({ identifier, password }),
      });
      storage.saveAuth(result.access_token, result.user);
      setUser(result.user);
    },
    register: async payload => api(endpoints.signup, { method: 'POST', auth: false, body: JSON.stringify(payload) }),
    refresh,
    logout: () => { storage.clearAuth(); setUser(null); },
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
